/* Regional free-shipping guidance. No geolocation, third-party requests or analytics. */
const BRAZIL_STATES = new Set(['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO']);
export function shippingProgress(total, threshold) {
  const amount = Math.max(0, Number(total) || 0);
  return { remaining: Math.max(0, threshold - amount), percent: Math.min(100, amount / threshold * 100) };
}
export function shippingThreshold(state) {
  return BRAZIL_STATES.has(state) ? 24901 : null;
}

if (typeof window !== 'undefined' && !customElements.get('decolab-shipping-progress')) {
  const money = value => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100);
  const sessionKey = 'decolab.shipping.state.v1';
  let currentCart = null;
  let pendingCart = null;
  let currentState = '';
  let stateInitialized = false;
  try {
    const saved = sessionStorage.getItem(sessionKey);
    currentState = saved || '';
    stateInitialized = saved !== null;
  } catch { /* private browsing */ }
  if (!BRAZIL_STATES.has(currentState)) currentState = '';
  const instances = new Set();
  function refreshCart() {
    if (pendingCart) return pendingCart;
    pendingCart = fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(response => { if (!response.ok) throw new Error('Cart unavailable'); return response.json(); })
      .then(cart => { currentCart = cart; instances.forEach(instance => instance.render()); })
      .catch(() => instances.forEach(instance => instance.showFallback()))
      .finally(() => { pendingCart = null; });
    return pendingCart;
  }
  document.addEventListener('cart:update', event => {
    if (event.detail?.data?.didError) return;
    // CartAddEvent can contain only the added item. Read the authoritative cart total.
    refreshCart();
  });
  document.addEventListener('discount:update', refreshCart);
  // Dialog events do not bubble in Horizon. Capture opening and refresh after morph.
  document.addEventListener('dialog:open', event => {
    if (event.target?.matches?.('cart-drawer-component')) refreshCart();
  }, true);
  window.addEventListener('pageshow', event => { if (event.persisted) refreshCart(); });

  customElements.define('decolab-shipping-progress', class extends HTMLElement {
    static get observedAttributes() { return ['data-cart-total', 'data-cart-count']; }
    attributeChangedCallback() { if (this.isConnected) this.scheduleRender(); }
    scheduleRender() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => { if (this.isConnected) this.render(); });
    }
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      instances.add(this);
      if (!stateInitialized) {
        if (this.dataset.country === 'BR' && BRAZIL_STATES.has(this.dataset.state)) currentState = this.dataset.state;
        stateInitialized = true;
      }
      this.addEventListener('change', event => {
        if (!event.target.matches('select')) return;
        currentState = BRAZIL_STATES.has(event.target.value) ? event.target.value : '';
        try { sessionStorage.setItem(sessionKey, currentState); } catch { /* optional */ }
        instances.forEach(instance => instance.render());
        this.querySelector('details').open = false;
      }, { signal: this.abort.signal });
      this.observer?.disconnect();
      // A theme morph can replace children without reconnecting the custom element.
      // Only direct children are observed; rendering text inside them cannot loop.
      this.observer = new MutationObserver(() => this.scheduleRender());
      this.observer.observe(this, { childList: true });
      this.scheduleRender();
      refreshCart();
    }
    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); cancelAnimationFrame(this.frame); instances.delete(this); }
    showFallback() {
      if (!this.querySelector('[data-shipping-context]') || !this.querySelector('[data-shipping-note]')) return;
      this.querySelector('[data-shipping-context]').textContent = 'Frete grátis acima de R$ 249 em todo o Brasil.';
      this.querySelectorAll('[data-shipping-row]').forEach(row => { row.hidden = true; });
      this.querySelector('[data-shipping-note]').textContent = 'Mínimo em produtos após descontos. Confira o benefício no checkout.';
    }
    render() {
      if (!this.querySelector('select') || !this.querySelector('[data-shipping-note]')) return;
      if (currentCart?.currency && currentCart.currency !== 'BRL') { this.hidden = true; return; }
      const itemCount = currentCart?.item_count ?? Number(this.dataset.cartCount || 0);
      this.hidden = this.dataset.context !== 'cart' || itemCount === 0;
      if (this.hidden) return;
      const total = currentCart?.total_price ?? Number(this.dataset.cartTotal || 0);
      const threshold = shippingThreshold(currentState);
      const welcomeApplied = currentCart?.cart_level_discount_applications?.some(discount => String(discount.title).toUpperCase() === 'BEMVINDO10');
      this.querySelector('select').value = currentState;
      this.querySelector('[data-shipping-region]').textContent = currentState ? `Entrega: ${currentState} · alterar` : 'Informar estado';
      this.querySelector('[data-shipping-context]').textContent = welcomeApplied ? 'BEMVINDO10 aplicado: não acumula com frete grátis.' : 'Sobre o valor dos produtos após descontos';
      this.querySelectorAll('[data-shipping-row]').forEach(row => {
        const target = Number(row.dataset.threshold);
        row.hidden = welcomeApplied || (threshold !== null && threshold !== target);
        const progress = shippingProgress(total, target);
        const region = threshold ? currentState : row.dataset.region;
        this.querySelector('[data-shipping-title]').textContent = 'Frete grátis';
        row.querySelector('[data-shipping-label]').textContent = threshold ? 'Acima de R$ 249' : `${region} · acima de R$ 249`;
        row.querySelector('[data-shipping-remaining]').textContent = progress.remaining > 0 ? `Faltam ${money(progress.remaining)}` : 'Mínimo de frete grátis alcançado';
        const meter = row.querySelector('[role="progressbar"]');
        meter.setAttribute('aria-valuenow', String(Math.round(progress.percent)));
        meter.setAttribute('aria-valuetext', `${region}: ${money(Math.min(total, target))} de ${money(target)}`);
        meter.querySelector('span').style.width = progress.percent + '%';
      });
      this.querySelector('[data-shipping-note]').textContent = 'Não acumula com BEMVINDO10. Confirme o benefício para seu endereço no checkout.';
    }
  });
}
